import { SOURCE_CAROUSEL_ID } from './constants';

const TOAST_TYPE_NORMALIZED = SOURCE_CAROUSEL_ID.TOAST_BANNER.toLowerCase();

/**
 * Extra pages `type` values (v4/v5) that share the toast-banner communication contract.
 * Keep in sync with backend; extend here instead of scattering string checks.
 */
const DIALOG_COMMUNICATION_TYPES_NORMALIZED = new Set([
  TOAST_TYPE_NORMALIZED,
  'dialog-banner',
  'dialog-ads',
  'communication-dialog',
  'dialog',
]);

export function normalizeRowTypeString(type) {
  if (!type || typeof type !== 'string') return '';
  return type.trim().toLowerCase().replace(/_/g, '-');
}

const ROW_ADS_COMMUNICATION_TYPES_NORMALIZED = new Set([
  'dialog-ads',
  normalizeRowTypeString(SOURCE_CAROUSEL_ID.ROW_ADS),
]);

/**
* @param {object|undefined} carouselInfo
* @param {string} key
* @returns {unknown}
*/
export function readCarouselField(carouselInfo, key) {
  if (carouselInfo === undefined || carouselInfo === null) return undefined;
  if (typeof carouselInfo.get === 'function') {
    return carouselInfo.get(key);
  }
  return carouselInfo[key];
}

/**
 * @param {object|undefined} carouselInfo
 * @returns {string|undefined}
 */
export function readCarouselRowType(carouselInfo) {
  if (carouselInfo === undefined || carouselInfo === null) return undefined;
  const direct = readCarouselField(carouselInfo, 'type');
  if (typeof direct === 'string' && direct.trim()) return direct;
  const element = readCarouselField(carouselInfo, 'element');
  const fromElement = readCarouselField(element, 'type');
  if (typeof fromElement === 'string' && fromElement.trim()) return fromElement;
  const carousel = readCarouselField(carouselInfo, 'carousel');
  const fromCarousel = readCarouselField(carousel, 'type');
  if (typeof fromCarousel === 'string' && fromCarousel.trim()) return fromCarousel;
  return undefined;
}

export function isHomeBannerRowType(type) {
  if (!type || typeof type !== 'string') return false;
  return normalizeRowTypeString(type) === SOURCE_CAROUSEL_ID.HOME_BANNER;
}

/**
 * @param {string|undefined} type pages element / carousel `type`
 * @returns {boolean} true if this row should use communication (dialog/toast) like toast-banner
 */
export function isToastBannerRowType(type) {
  if (!type || typeof type !== 'string') return false;
  const normalized = normalizeRowTypeString(type);
  return DIALOG_COMMUNICATION_TYPES_NORMALIZED.has(normalized);
}

/**
 * @param {string|undefined} type
 * @returns {boolean}
 */
export function isRowAdsCommunicationRowType(type) {
  if (!type || typeof type !== 'string') return false;
  return ROW_ADS_COMMUNICATION_TYPES_NORMALIZED.has(normalizeRowTypeString(type));
}

/**
 * @param {string|undefined} rowType resultado de `readCarouselRowType`
 * @param {boolean} toastActive `VITE_IS_TOAST_BANNER_ACTIVE`
 * @param {boolean} rowAdsActive `VITE_IS_ROW_ADS_ACTIVE`
 */
export function isCommunicationBannerFetchEnabledForRowType(rowType, toastActive, rowAdsActive) {
  if (!isToastBannerRowType(rowType)) return false;
  if (isRowAdsCommunicationRowType(rowType)) return Boolean(rowAdsActive);
  return Boolean(toastActive);
}

const TOAST_HERO_STACK_NEXT_TYPES_NORMALIZED = new Set([
  'ta-hero',
  'hero-now-next',
  'highlights',
  'live-hero-automatic',
  'live-hero-editorial',
  'carousel-landscape',
]);

export function isToastHeroStackFollowingType(type) {
  if (!type || typeof type !== 'string') return false;
  return TOAST_HERO_STACK_NEXT_TYPES_NORMALIZED.has(normalizeRowTypeString(type));
}

/**
 * Id for GET .../communication/{id} on lander rows.
 * Prefer explicit element id for communication rows when present in the payload.
 *
 * @param {object|undefined} carouselInfo page.carousels / page.elements entry (Immutable or POJO)
 * @returns {string}
 */
export function getCarouselRowBannerId(carouselInfo) {
  if (!carouselInfo) {
    return '';
  }
  const type = readCarouselRowType(carouselInfo);
  const elementId = readCarouselField(carouselInfo, 'elementId');
  const sourceCarouselId = readCarouselField(carouselInfo, 'sourceCarouselId');
  const id = readCarouselField(carouselInfo, 'id');
  const element = readCarouselField(carouselInfo, 'element');
  const carousel = readCarouselField(carouselInfo, 'carousel');
  const nestedElementId = readCarouselField(element, 'elementId');
  const nestedSourceId = readCarouselField(element, 'sourceCarouselId');
  const nestedId = readCarouselField(element, 'id') ?? readCarouselField(carousel, 'id');
  const nestedCarouselElementId = readCarouselField(carousel, 'elementId');
  const isCommunicationBanner = isToastBannerRowType(type)
    || isHomeBannerRowType(type);
  const raw = isCommunicationBanner
    ? (
      id
      ?? elementId
      ?? sourceCarouselId
      ?? nestedElementId
      ?? nestedSourceId
      ?? nestedId
      ?? nestedCarouselElementId
    )
    : (sourceCarouselId ?? elementId ?? id ?? nestedId);
  if (raw === undefined || raw === null || raw === '') return '';
  return String(raw).trim();
}
